
import {
  Action,
  Component,
  Getter,
  Prop,
  State,
  Watch,
} from 'nuxt-property-decorator';
import {
  ISanityDocuments,
  SanityDocumentSchema,
  ProjectPageVariation,
} from '~/types/sanity-documents';
import { IGamesState } from '~/store/games/types';
import PageHeroContainer from '~/components/UIComponents/PageHeroContainer.vue';
import SanityBlockContent from '~/components/Sanity/SanityBlockContent.vue';
import SanityReference from '~/components/Sanity/SanityReference.vue';
import SignUpForAlertsBtn from '~/components/UIComponents/SignUpForAlertsBtn.vue';
import WatchVideoDialog from '~/components/Sanity/WatchVideoDialog.vue';
import ShareButton from '~/components/Share/ShareButton.vue';
import ProjectSaleItemsProgress from './ProjectSaleItemsProgress.vue';
import { IProfileState } from '~/store/profile/types';
import ProjectMetadata from './ProjectMetadata.vue';
import { IProject, IProjectViews } from '~/types/project';
import { RZR_PROJECT_SLUG } from '~/store/projects/types';
import { EpisodeAction, IEpisodeWatchSuggestion } from '~/types/series';
import { Mixins } from 'vue-property-decorator';
import { AuthActions } from '~/mixins';
import RewardsStats from '~/components/Collections/RewardsStats.vue';
import { TStatsCard, EStatsCardType } from '~/types/collections';
import FilmTokenIcon from '~/components/Token/FilmTokenIcon.vue';
import { formatNumberWithCommas, getPercFormat } from '~/utils/format';
import LightningIcon from '~/components/SvgIcons/LightningIcon.vue';
import CustomBtn from '../UIComponents/CustomBtn.vue';
import RightCaret from '~/components/SvgIcons/RightCaret.vue';
import { IFilmRewardMetrics } from '~/types/my-collection';
import { IProjectBoostStats } from '~/store/boosts/types';
import StackerOverflowIcon from '~/components/SvgIcons/StackOverflowIcon.vue';

@Component({
  components: {
    SanityReference,
    PageHeroContainer,
    ProjectMetadata,
    ProjectSaleItemsProgress,
    SanityBlockContent,
    ShareButton,
    SignUpForAlertsBtn,
    WatchVideoDialog,
    RewardsStats,
    StackerOverflowIcon,
  },
})
export default class ProjectHero extends Mixins(AuthActions) {
  @Prop(Object) readonly doc!: ISanityDocuments[SanityDocumentSchema.PROJECT];
  @Prop(Boolean) readonly loading!: boolean;
  @Prop(Boolean) readonly isSaleLive!: boolean;
  @Prop(Object) readonly project!: IProject;

  @State((state: IGamesState) => state.itemsForSaleState, {
    namespace: 'games',
  })
  readonly itemsForSaleState!: IGamesState['itemsForSaleState'];

  @State((profile) => profile.user, { namespace: 'profile' })
  currentUser!: IProfileState['user'];

  @State((profile) => profile.user.referralCode, { namespace: 'profile' })
  readonly referralCode!: string;

  @State((rewards) => rewards.project, { namespace: 'rewards' })
  readonly projectRewards!: Record<string, IFilmRewardMetrics>;

  @Getter('getProjectEpisodeWatchSuggestion', { namespace: 'projects' })
  readonly getProjectEpisodeWatchSuggestion!: (
    projectSlug: string,
  ) => IEpisodeWatchSuggestion;

  @Getter('getProjectViews', { namespace: 'projects' })
  readonly getProjectViews!: (projectSlug: string) => IProjectViews | undefined;

  @Getter('getProjectBoostStats', { namespace: 'boosts' })
  readonly getProjectBoostStats!: (
    projectSlug: string,
  ) => IProjectBoostStats | null;

  @Action('showWatchSuggestionEpisode', { namespace: 'projects' })
  private showWatchSuggestionEpisode!: (projectSlug: string) => void;

  @Action('showRZREpisode1', { namespace: 'projects' })
  private showRZREpisode1!: () => void;

  @Action('getProjectRewards', { namespace: 'rewards' })
  private getProjectRewards!: (projectSlug: string) => void;

  @Action('getProjectFilmBoostStats', { namespace: 'boosts' })
  private getProjectFilmBoostStats!: (projectSlug: {
    category: string;
  }) => void;

  saleEnded = false;
  schemas = SanityDocumentSchema;
  variations = ProjectPageVariation;
  videoSchema = SanityDocumentSchema.VIDEO;

  @Watch('project.slug', { immediate: true })
  handleProjectSlug(current: string | undefined, previous: string | undefined) {
    if (current === previous || !current) {
      return;
    }
    this.getProjectRewards(current);
    this.getProjectFilmBoostStats({ category: current });
  }

  goToFilmBoost() {
    this.$router.push(`/film-boost/${this.project.slug}`);
  }

  goToProjectCollectionPage() {
    this.$router.push(`/collection/${this.project.slug}`);
  }

  get rewardItems() {
    const views = this.getViews;
    const boost = this.getBoostStats;
    const rewards = this.projectRewards[this.project.slug];

    const splitCardContainerClass =
      'bg-stats-card rounded-xl mt-6 mt-sm-10 pa-6 mr-0 mr-sm-8';
    const fullCardContainerClass = `bg-stats-card rounded-xl mt-6 mt-sm-10 mr-0 mr-sm-8 pa-6 d-flex flex-column justify-space-around`;
    const statsItems: TStatsCard[] = [];

    if (views) {
      statsItems.push({
        type: EStatsCardType.SPLIT,
        upperTitle: this.$t('components.collection.totalViews') as string,
        upperStatsText: formatNumberWithCommas(views?.totalViews || 0, 0),
        upperContainerClass:
          'd-flex align-center pb-4 flex-grow-1 justify-space-between',
        lowerTitle: this.$t('components.collection.lastDayViews') as string,
        lowerStatsText: formatNumberWithCommas(views?.lastDayViews || 0, 0),
        lowerContainerClass:
          'd-flex align-center pt-4 flex-grow-1 justify-space-between',
        containerClass: splitCardContainerClass,
      });
    }

    if (boost && this.doc?.enableFilmBoost) {
      statsItems.push({
        type: EStatsCardType.FULL,
        title: this.$t('components.collection.currentFilmBoost') as string,
        stats: getPercFormat(boost?.currentFilmBoostPerc || 0, 2),
        icon: LightningIcon,
        iconProps: { type: 'circular-lightning' },
        rightSection: this.$vuetify.breakpoint.lgAndUp ? CustomBtn : RightCaret,
        rightSectionProps: {
          class: 'font--button rounded-pill ml-6 pointer',
          text: this.$t('components.collection.boostNow') as string,
          on: { click: this.goToFilmBoost },
        },
        mode: 'enabled',
        cardContainerClass: fullCardContainerClass,
      });
    }

    if (rewards && this.doc?.showRewardsOnFilmPage) {
      statsItems.push({
        type: EStatsCardType.SPLIT,
        upperTitle: this.$t('components.collection.totalRewards') as string,
        upperStatsText: formatNumberWithCommas(rewards?.totalRewards, 0),
        upperContainerClass:
          'd-flex align-center pb-4 flex-grow-1 justify-space-between',
        upperStatsLeftIcon: FilmTokenIcon,
        upperStatsLeftIconProps: { width: 20, height: 20, class: 'mr-2' },
        lowerTitle: this.$t('components.collection.lastDayRewards') as string,
        lowerStatsText: formatNumberWithCommas(rewards?.lastDayRewards, 0),
        lowerStatsLeftIcon: FilmTokenIcon,
        lowerStatsLeftIconProps: { width: 20, height: 20, class: 'mr-2' },
        lowerContainerClass:
          'd-flex align-center pt-4 flex-grow-1 justify-space-between',
        containerClass: splitCardContainerClass,
      });
    }
    return statsItems;
  }

  get episodeWatchSuggestion() {
    return this.getProjectEpisodeWatchSuggestion(this.project?.slug);
  }

  get getViews() {
    return this.getProjectViews(this.project?.slug);
  }

  get getBoostStats() {
    return this.getProjectBoostStats(this.project?.slug);
  }

  get watchEpisodeButtonText() {
    if (!this.episodeWatchSuggestion) {
      return '';
    }

    if (!this.project.hasMultipleEpisodes) {
      return this.episodeWatchSuggestion.episodeAction === EpisodeAction.RESUME
        ? this.$t('common.video.resume')
        : this.$t('common.video.watch');
    }

    const actionText =
      this.episodeWatchSuggestion.episodeAction === EpisodeAction.RESUME
        ? 'Resume'
        : 'Watch';
    const episodeNumber = this.episodeWatchSuggestion.episodeNumber;
    return `${actionText} E${episodeNumber}`;
  }

  get variation() {
    return this.$sanityLookup.getSiteConfig()?.data?.projectPageVariation;
  }

  get activeCampaignTag() {
    return this.doc?.activeCampaignId ? this.doc?.activeCampaignId : '66';
  }

  get backgroundImage() {
    if (!this.doc?.pageHeroImage) {
      return;
    }

    const imageSource = {
      asset: this.doc.pageHeroImage.asset,
    };

    return this.$sanityImage
      .urlFor(imageSource)
      .width(1920)
      .height(Math.round(1920 * (9 / 16)))
      .url();
  }

  get genres() {
    return this.doc?.genres || [];
  }

  get hasActiveCampaignTag() {
    return this.doc?.activeCampaignId;
  }

  get trailerId() {
    return this.doc?.trailer?._ref;
  }

  get logo() {
    if (!this.doc?.logo) {
      return;
    }

    return this.$sanityImage.urlFor(this.doc.logo).url();
  }

  get releaseDateDisplayText() {
    return this.doc?.releaseDateDisplayText;
  }

  get showAlertSignupButton() {
    return !this.doc?.contentAvailability?.isWatchable;
  }

  get shareUrl() {
    const append = this.referralCode
      ? `?source=gala&referralCode=${this.referralCode}`
      : '';
    return `${window.location.origin}${this.$route.path}${append}`;
  }

  get tags() {
    return this.doc?.overviewTags || [];
  }

  get title() {
    return this.doc?.title || '';
  }

  get titleBadges() {
    return this.doc?.titleBadges || [];
  }

  handleShowNextEpisodeClick() {
    if (!this.currentUser?.id) {
      if (this.project.slug === RZR_PROJECT_SLUG) {
        this.showRZREpisode1();
        return;
      }
      this.goToLoginService();
      return;
    }

    this.showWatchSuggestionEpisode(this.project.slug);
  }

  goToLoginService() {
    return this.promptToLogin({
      redirectAfterLoginPath: this.$route.fullPath,
    });
  }
}
